import React, { useEffect, useState, useMemo } from 'react';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, LineElement, PointElement, Title, Tooltip, Legend } from 'chart.js';
import { Bar } from 'react-chartjs-2';
import { institutionApi, form4Api } from '../services/api';

ChartJS.register(CategoryScale, LinearScale, BarElement, LineElement, PointElement, Title, Tooltip, Legend);

/* ---------- Interfaces ---------- */
interface QuarterlyChange {
  label: string; ticker: string; name: string;
  oldShares: number; newShares: number; changePct: number;
}
interface QuarterlyNewOrSold {
  label: string; ticker: string; name: string;
  shares: number; value: number | null;
}
interface QuarterlyData {
  currentQuarter: string; lastQuarter: string;
  increased: QuarterlyChange[]; decreased: QuarterlyChange[];
  newPositions: QuarterlyNewOrSold[]; soldOut: QuarterlyNewOrSold[];
}
interface TopHolder {
  filerCIK: string | null; filerName: string; ticker: string;
  name: string; shares: number; value: number | null;
}
interface OwnershipLog { quarter: string; ticker: string; name: string; shares: number; }
interface SearchData {
  ticker: string; currentHolders: number; lastHolders: number;
  currentShares: number; lastShares: number; outstandingShares: number | null;
  topHolders: TopHolder[]; logs: OwnershipLog[];
  increased: QuarterlyChange[]; decreased: QuarterlyChange[];
  newPositions: QuarterlyNewOrSold[]; soldOut: QuarterlyNewOrSold[];
}
interface DashboardData {
  quarterly: { quarter: string; tickerCount: number; totalShares: number }[];
  topWidelyHeld: { ticker: string; count: number; shares: number }[];
  topByShares: { ticker: string; count: number; shares: number }[];
}
interface DateInfo { date: string; count: number; filers: number; }
interface StatsData {
  totalRecords: number; latestRecords: number; lastqRecords: number;
  totalFilers: number; minDate: string | null; maxDate: string | null;
}

type TabKey = 'dashboard' | 'summary' | 'portfolio' | 'search' | 'download' | 'backfill' | 'maintenance';

/* ---------- Helpers ---------- */
const fmt = (n: number) => n.toLocaleString();
const yahooUrl = (ticker: string) => `https://finance.yahoo.com/quote/${ticker}`;

const copyToClipboard = (text: string) => {
  navigator.clipboard.writeText(text).catch(() => {
    const ta = document.createElement('textarea');
    ta.value = text;
    document.body.appendChild(ta);
    ta.select();
    document.execCommand('copy');
    document.body.removeChild(ta);
  });
};

/* ---------- Tweet generators ---------- */
const tweetChangeTable = (title: string, items: QuarterlyChange[], type: 'increased' | 'decreased') => {
  if (!items.length) return '';
  const emoji = type === 'increased' ? '\uD83D\uDFE2' : '\uD83D\uDD34';
  const lines = items.slice(0, 20).map((item) => {
    const sharesStr = item.newShares >= 1_000_000 ? `${(item.newShares / 1_000_000).toFixed(1)}M` : fmt(item.newShares);
    const sign = type === 'increased' ? '+' : '-';
    return `$${item.ticker} — ${item.label} ${sharesStr} shares (${sign}${item.changePct}%)`;
  });
  const tickers = Array.from(new Set(items.slice(0, 10).map(t => `$${t.ticker}`))).join(' ');
  return `${emoji} 13F Institutional Ownership: ${title} (${items.length})\n\n${lines.join('\n')}\n\n${tickers}\n\n#13F #InstitutionalOwnership #SEC #WallStreet`;
};

const tweetNewSoldTable = (title: string, items: QuarterlyNewOrSold[], type: 'new' | 'sold') => {
  if (!items.length) return '';
  const emoji = type === 'new' ? '\uD83D\uDFE2' : '\uD83D\uDD34';
  const lines = items.slice(0, 20).map((item) => {
    const sharesStr = item.shares >= 1_000_000 ? `${(item.shares / 1_000_000).toFixed(1)}M` : fmt(item.shares);
    const valStr = item.value ? ` ($${fmt(item.value)}K)` : '';
    return `$${item.ticker} — ${item.label} ${sharesStr} shares${valStr}`;
  });
  const tickers = Array.from(new Set(items.slice(0, 10).map(t => `$${t.ticker}`))).join(' ');
  return `${emoji} 13F Institutional Ownership: ${title} (${items.length})\n\n${lines.join('\n')}\n\n${tickers}\n\n#13F #InstitutionalOwnership #SEC #WallStreet`;
};

const tweetTopHolders = (ticker: string, holders: TopHolder[]) => {
  if (!holders.length) return '';
  const lines = holders.slice(0, 20).map((h, i) => {
    const sharesStr = h.shares >= 1_000_000 ? `${(h.shares / 1_000_000).toFixed(1)}M` : fmt(h.shares);
    return `${i + 1}. ${h.filerName} — ${sharesStr} shares`;
  });
  return `\uD83C\uDFE6 Top Institutional Holders of $${ticker}\n\n${lines.join('\n')}\n\n#13F #InstitutionalOwnership #SEC $${ticker}`;
};

/* ---------- Post Button ---------- */
const PostButton: React.FC<{ getText: () => string }> = ({ getText }) => {
  const [status, setStatus] = useState<'idle' | 'copied' | 'posting' | 'posted' | 'error'>('idle');
  const handleCopy = () => { copyToClipboard(getText()); setStatus('copied'); setTimeout(() => setStatus('idle'), 2000); };
  const handlePost = () => {
    const text = getText();
    if (!window.confirm(`Post to X?\n\n${text}`)) return;
    setStatus('posting');
    form4Api.postToX(text, false)
      .then(() => { setStatus('posted'); setTimeout(() => setStatus('idle'), 5000); })
      .catch((err: any) => { setStatus('error'); alert(`Post failed: ${err?.response?.data?.detail || err.message}`); setTimeout(() => setStatus('idle'), 3000); });
  };
  const postColor = status === 'posted' ? 'green' : status === 'error' ? 'red' : '#e04530';
  const postLabel = status === 'posting' ? 'Posting...' : status === 'posted' ? 'Posted!' : status === 'error' ? 'Failed' : 'Post to X';
  return (
    <span style={{ marginLeft: '8px', fontSize: '0.75em' }}>
      <span style={{ cursor: status === 'posting' ? 'wait' : 'pointer', color: postColor, fontWeight: 'bold', marginRight: 6 }}
        onClick={status === 'posting' ? undefined : handlePost}>{postLabel}</span>
      <span style={{ cursor: 'pointer', color: status === 'copied' ? 'green' : '#888' }}
        onClick={handleCopy}>{status === 'copied' ? 'Copied!' : 'Copy'}</span>
    </span>
  );
};

/* ---------- Sortable Change Table ---------- */
type ChangeSortKey = 'label' | 'ticker' | 'name' | 'oldShares' | 'newShares' | 'changePct';
const ChangeTable: React.FC<{ title: string; items: QuarterlyChange[]; type: 'increased' | 'decreased'; tab: TabKey; onTickerClick?: (t: string) => void }> = ({ title, items, type, tab, onTickerClick }) => {
  const [sortKey, setSortKey] = useState<ChangeSortKey>('changePct');
  const [sortAsc, setSortAsc] = useState(false);
  const handleSort = (k: ChangeSortKey) => { if (sortKey === k) setSortAsc(!sortAsc); else { setSortKey(k); setSortAsc(k === 'label' || k === 'ticker' || k === 'name'); } };
  const arrow = (k: ChangeSortKey) => sortKey === k ? (sortAsc ? ' \u25B2' : ' \u25BC') : '';
  const sorted = useMemo(() => {
    const a = [...items]; const d = sortAsc ? 1 : -1;
    a.sort((x, y) => { const xv = (x as any)[sortKey]; const yv = (y as any)[sortKey]; return xv < yv ? -d : xv > yv ? d : 0; });
    return a;
  }, [items, sortKey, sortAsc]);
  if (!items.length) return null;
  const color = type === 'increased' ? 'green' : 'red';
  const thS = (k: ChangeSortKey, label: string) => <th style={{ ...styles.th, cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort(k)}>{label}{arrow(k)}</th>;
  return (
    <>
      <h4 style={{ ...styles.sectionTitle, color }}>{title} ({items.length})<PostButton getText={() => tweetChangeTable(title, items, type)} /></h4>
      <table style={styles.table}><thead><tr>{thS('label', 'Label')}{thS('ticker', 'Ticker')}{thS('name', 'Name')}{thS('oldShares', 'Old Shares')}{thS('newShares', 'New Shares')}{thS('changePct', 'Change %')}</tr></thead>
        <tbody>{sorted.map((item, idx) => (
          <tr key={idx} style={{ backgroundColor: idx % 2 === 0 ? '#fff' : '#e6f2ff' }}>
            <td style={styles.td}>{item.label}</td>
            <td style={styles.td}>{tab === 'search' ? <a href={yahooUrl(item.ticker)} target="_blank" rel="noreferrer" style={{ color: '#0069cc', textDecoration: 'none', fontWeight: 'bold' }}>{item.ticker}</a> : onTickerClick ? <span style={{ ...styles.actionLink, fontWeight: 'bold' }} onClick={() => onTickerClick(item.ticker)}>{item.ticker}</span> : <strong>{item.ticker}</strong>}</td>
            <td style={styles.td}>{item.name}</td>
            <td style={{ ...styles.td, textAlign: 'right' }}>{fmt(item.oldShares)}</td>
            <td style={{ ...styles.td, textAlign: 'right' }}>{fmt(item.newShares)}</td>
            <td style={{ ...styles.td, textAlign: 'right', fontWeight: 'bold', color }}>{type === 'increased' ? '+' : '-'}{item.changePct}%</td>
          </tr>
        ))}</tbody>
      </table>
    </>
  );
};

/* ---------- Sortable New/Sold Table ---------- */
type NewSoldSortKey = 'label' | 'ticker' | 'name' | 'shares' | 'value';
const NewSoldTable: React.FC<{ title: string; items: QuarterlyNewOrSold[]; type: 'new' | 'sold'; tab: TabKey; onTickerClick?: (t: string) => void }> = ({ title, items, type, tab, onTickerClick }) => {
  const [sortKey, setSortKey] = useState<NewSoldSortKey>('shares');
  const [sortAsc, setSortAsc] = useState(false);
  const handleSort = (k: NewSoldSortKey) => { if (sortKey === k) setSortAsc(!sortAsc); else { setSortKey(k); setSortAsc(k === 'label' || k === 'ticker' || k === 'name'); } };
  const arrow = (k: NewSoldSortKey) => sortKey === k ? (sortAsc ? ' \u25B2' : ' \u25BC') : '';
  const sorted = useMemo(() => {
    const a = [...items]; const d = sortAsc ? 1 : -1;
    a.sort((x, y) => { const xv = (x as any)[sortKey] ?? 0; const yv = (y as any)[sortKey] ?? 0; return xv < yv ? -d : xv > yv ? d : 0; });
    return a;
  }, [items, sortKey, sortAsc]);
  if (!items.length) return null;
  const color = type === 'new' ? '#0069cc' : '#cc6900';
  const thS = (k: NewSoldSortKey, label: string) => <th style={{ ...styles.th, cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort(k)}>{label}{arrow(k)}</th>;
  return (
    <>
      <h4 style={{ ...styles.sectionTitle, color }}>{title} ({items.length})<PostButton getText={() => tweetNewSoldTable(title, items, type)} /></h4>
      <table style={styles.table}><thead><tr>{thS('label', 'Label')}{thS('ticker', 'Ticker')}{thS('name', 'Name')}{thS('shares', 'Shares')}{thS('value', 'Value')}</tr></thead>
        <tbody>{sorted.map((item, idx) => (
          <tr key={idx} style={{ backgroundColor: idx % 2 === 0 ? '#fff' : '#e6f2ff' }}>
            <td style={styles.td}>{item.label}</td>
            <td style={styles.td}>{tab === 'search' ? <a href={yahooUrl(item.ticker)} target="_blank" rel="noreferrer" style={{ color: '#0069cc', textDecoration: 'none', fontWeight: 'bold' }}>{item.ticker}</a> : onTickerClick ? <span style={{ ...styles.actionLink, fontWeight: 'bold' }} onClick={() => onTickerClick(item.ticker)}>{item.ticker}</span> : <strong>{item.ticker}</strong>}</td>
            <td style={styles.td}>{item.name}</td>
            <td style={{ ...styles.td, textAlign: 'right' }}>{fmt(item.shares)}</td>
            <td style={{ ...styles.td, textAlign: 'right' }}>{item.value ? '$' + fmt(item.value) : '-'}</td>
          </tr>
        ))}</tbody>
      </table>
    </>
  );
};

/* ---------- Dashboard Tab ---------- */
const TopInstTable: React.FC<{ title: string; items: { ticker: string; count: number; shares: number }[]; color: string; onTickerClick?: (t: string) => void }> = ({ title, items, color, onTickerClick }) => (
  <div>
    <div style={{ fontWeight: 'bold', fontSize: '0.9em', marginBottom: '0.3em', color }}>{title}</div>
    <table style={styles.table}>
      <thead><tr><th style={styles.th}>#</th><th style={styles.th}>Ticker</th><th style={styles.th}>Filers</th><th style={styles.th}>Total Shares</th></tr></thead>
      <tbody>
        {items.map((t, i) => (
          <tr key={t.ticker} style={{ backgroundColor: i % 2 === 0 ? '#fff' : '#e6f2ff' }}>
            <td style={{ ...styles.td, textAlign: 'center' }}>{i + 1}</td>
            <td style={styles.td}>{onTickerClick ? <span style={{ ...styles.actionLink, fontWeight: 'bold' }} onClick={() => onTickerClick(t.ticker)}>{t.ticker}</span> : <strong>{t.ticker}</strong>}</td>
            <td style={{ ...styles.td, textAlign: 'right' }}>{fmt(t.count)}</td>
            <td style={{ ...styles.td, textAlign: 'right' }}>{fmt(t.shares)}</td>
          </tr>
        ))}
        {items.length === 0 && <tr><td colSpan={4} style={{ ...styles.td, color: '#999' }}>No data</td></tr>}
      </tbody>
    </table>
  </div>
);

const DashboardTab: React.FC<{ onSearchTicker?: (t: string) => void }> = ({ onSearchTicker }) => {
  const [data, setData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    institutionApi.dashboard().then(r => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);
  if (loading) return <div style={{ textAlign: 'center', padding: '2em' }}>Loading Dashboard...</div>;
  if (!data) return <div style={{ textAlign: 'center', padding: '2em' }}>Failed to load dashboard.</div>;

  const labels = data.quarterly.map(q => q.quarter);
  const chartOpts: any = { responsive: true, plugins: { legend: { display: false } } };
  const tickerData = { labels, datasets: [{ data: data.quarterly.map(q => q.tickerCount), backgroundColor: '#4e79a7' }] };
  const sharesData = { labels, datasets: [{ data: data.quarterly.map(q => q.totalShares), backgroundColor: '#59a14f' }] };

  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1em', marginBottom: '1em' }}>
        <div style={{ height: 250, padding: '8px', border: '1px solid #ddd', borderRadius: 4 }}>
          <div style={{ fontWeight: 'bold', fontSize: '0.85em', marginBottom: 4 }}>Tickers Tracked by Quarter</div>
          <Bar data={tickerData} options={chartOpts} />
        </div>
        <div style={{ height: 250, padding: '8px', border: '1px solid #ddd', borderRadius: 4 }}>
          <div style={{ fontWeight: 'bold', fontSize: '0.85em', marginBottom: 4 }}>Total Institutional Shares by Quarter</div>
          <Bar data={sharesData} options={chartOpts} />
        </div>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1em' }}>
        <TopInstTable title="Top 10 Most Widely Held (by # of Filers)" items={data.topWidelyHeld} color="#0069cc" onTickerClick={onSearchTicker} />
        <TopInstTable title="Top 10 by Total Shares Held" items={data.topByShares} color="green" onTickerClick={onSearchTicker} />
      </div>
    </div>
  );
};

/* ---------- Progress Bar ---------- */
const ProgressBar: React.FC<{ current: number; total: number; label: string; color?: string }> = ({ current, total, label, color = '#4e79a7' }) => {
  const pct = total > 0 ? Math.round((current / total) * 100) : 0;
  return (
    <div style={{ marginBottom: '0.5em' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.8em', marginBottom: 2 }}>
        <span>{label}</span>
        <span>{current}/{total} ({pct}%)</span>
      </div>
      <div style={{ width: '100%', height: 18, backgroundColor: '#e0e0e0', borderRadius: 4, overflow: 'hidden' }}>
        <div style={{ width: `${pct}%`, height: '100%', backgroundColor: color, borderRadius: 4, transition: 'width 0.2s ease' }} />
      </div>
    </div>
  );
};

/* ---------- Download Tab ---------- */
const DownloadTab: React.FC = () => {
  const [reportDate, setReportDate] = useState(() => new Date().toLocaleDateString('en-CA', { timeZone: 'America/New_York' }));
  const [downloading, setDownloading] = useState(false);
  const [messages, setMessages] = useState<string[]>([]);
  const [dates, setDates] = useState<DateInfo[]>([]);
  const [loadingDates, setLoadingDates] = useState(true);
  const [dlProgress, setDlProgress] = useState<{ current: number; total: number } | null>(null);
  const [parseProgress, setParseProgress] = useState<{ current: number; total: number } | null>(null);

  const loadDates = () => { setLoadingDates(true); institutionApi.dates().then(r => setDates(r.data)).catch(() => {}).finally(() => setLoadingDates(false)); };
  useEffect(() => { loadDates(); }, []);

  const handleDownload = () => {
    if (!reportDate) return;
    setDownloading(true);
    setMessages([]);
    setDlProgress(null);
    setParseProgress(null);
    const token = localStorage.getItem('token');
    fetch(`/api/institutions/download?report_date=${reportDate}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(async (response) => {
        if (!response.ok) { const b = await response.json().catch(() => ({})); throw new Error(b.detail || `HTTP ${response.status}`); }
        const reader = response.body?.getReader();
        if (!reader) throw new Error('No reader');
        const decoder = new TextDecoder();
        let buffer = '';
        const read = (): Promise<void> => reader.read().then(({ done, value }) => {
          if (done) { setDownloading(false); loadDates(); return; }
          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n'); buffer = lines.pop() || '';
          for (const line of lines) {
            if (line.startsWith('data: ')) {
              try {
                const evt = JSON.parse(line.slice(6));
                if (evt.type === 'status') setMessages(p => [...p, evt.message]);
                else if (evt.type === 'download_total') setDlProgress({ current: 0, total: evt.total });
                else if (evt.type === 'download_progress') setDlProgress({ current: evt.current, total: evt.total });
                else if (evt.type === 'download_done') { setDlProgress(null); setMessages(p => [...p, `Downloaded ${evt.count} 13F files.`]); }
                else if (evt.type === 'parse_progress') setParseProgress({ current: evt.current, total: evt.total });
                else if (evt.type === 'done') { setParseProgress(null); setMessages(p => [...p, `Done! ${evt.downloaded} files, ${evt.parsed} parsed, ${evt.errors} errors.`]); }
                else if (evt.type === 'error') setMessages(p => [...p, `Error: ${evt.message}`]);
              } catch {}
            }
          }
          return read();
        });
        return read();
      })
      .catch((err) => { setMessages(p => [...p, `Error: ${err.message}`]); setDownloading(false); });
  };

  return (
    <>
      <div style={styles.searchBar}>
        <label>Report Date: </label>
        <input type="date" value={reportDate} onChange={e => setReportDate(e.target.value)} style={styles.input} />
        <input type="button" value={downloading ? 'Downloading...' : 'Download 13F'} onClick={handleDownload} disabled={downloading} style={{ ...styles.button, marginLeft: '1em' }} />
      </div>
      {dlProgress && <ProgressBar current={dlProgress.current} total={dlProgress.total} label="Downloading files..." color="#4e79a7" />}
      {parseProgress && <ProgressBar current={parseProgress.current} total={parseProgress.total} label="Parsing files..." color="#59a14f" />}
      {messages.length > 0 && (
        <div style={{ backgroundColor: '#f5f5f5', padding: '8px 12px', fontSize: '0.8em', maxHeight: '15em', overflow: 'auto', border: '1px solid #ddd', marginBottom: '1em', fontFamily: 'monospace' }}>
          {messages.map((m, i) => <div key={i}>{m}</div>)}
        </div>
      )}
      {loadingDates ? <div>Loading dates...</div> : (
        <table style={{ ...styles.table, width: '50%' }}>
          <thead><tr><th style={styles.th}>Date</th><th style={styles.th}>Records</th><th style={styles.th}>Filers</th></tr></thead>
          <tbody>{dates.map((d, i) => (
            <tr key={d.date} style={{ backgroundColor: i % 2 === 0 ? '#fff' : '#e6f2ff' }}>
              <td style={styles.td}>{d.date}</td>
              <td style={{ ...styles.td, textAlign: 'right' }}>{fmt(d.count)}</td>
              <td style={{ ...styles.td, textAlign: 'right' }}>{fmt(d.filers)}</td>
            </tr>
          ))}</tbody>
        </table>
      )}
    </>
  );
};

/* ---------- Backfill Tab ---------- */
const BackfillTab: React.FC = () => {
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [running, setRunning] = useState(false);
  const [messages, setMessages] = useState<string[]>([]);
  const [dayProgress, setDayProgress] = useState<{ current: number; total: number; date: string } | null>(null);
  const [parseProgress, setParseProgress] = useState<{ current: number; total: number } | null>(null);

  const handleBackfill = () => {
    setRunning(true);
    setMessages([]);
    setDayProgress(null);
    setParseProgress(null);
    const token = localStorage.getItem('token');
    const params = startDate && endDate ? `?start_date=${startDate}&end_date=${endDate}` : '';
    fetch(`/api/institutions/backfill${params}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(async (response) => {
        if (!response.ok) { const b = await response.json().catch(() => ({})); throw new Error(b.detail || `HTTP ${response.status}`); }
        const reader = response.body?.getReader();
        if (!reader) throw new Error('No reader');
        const decoder = new TextDecoder();
        let buffer = '';
        const read = (): Promise<void> => reader.read().then(({ done, value }) => {
          if (done) { setRunning(false); return; }
          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n'); buffer = lines.pop() || '';
          for (const line of lines) {
            if (line.startsWith('data: ')) {
              try {
                const evt = JSON.parse(line.slice(6));
                if (evt.type === 'date_start') {
                  setDayProgress({ current: evt.dayNum, total: evt.totalDays, date: evt.date });
                  setParseProgress(null);
                  setMessages(p => [...p, `Processing ${evt.date}...`]);
                }
                else if (evt.type === 'backfill_parse_progress') setParseProgress({ current: evt.current, total: evt.total });
                else if (evt.type === 'date_done') {
                  setParseProgress(null);
                  setMessages(p => { const m = [...p]; m[m.length - 1] = `${evt.date}: ${evt.files} files, ${evt.parsed} parsed`; return m; });
                }
                else if (evt.type === 'done') { setDayProgress(null); setParseProgress(null); setMessages(p => [...p, `Backfill complete! ${evt.totalParsed} total filings parsed.`]); }
                else if (evt.type === 'error') setMessages(p => [...p, `Error: ${evt.message}`]);
              } catch {}
            }
          }
          return read();
        });
        return read();
      })
      .catch((err) => { setMessages(p => [...p, `Error: ${err.message}`]); setRunning(false); });
  };

  return (
    <>
      <div style={styles.searchBar}>
        <label>Start: </label>
        <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} style={styles.input} />
        <label style={{ marginLeft: '1em' }}>End: </label>
        <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} style={styles.input} />
        <input type="button" value={running ? 'Running...' : 'Backfill'} onClick={handleBackfill} disabled={running} style={{ ...styles.button, marginLeft: '1em' }} />
        <span style={{ marginLeft: '1em', fontSize: '0.8em', color: '#666' }}>Leave dates empty for auto-detect (from last date to yesterday)</span>
      </div>
      {dayProgress && <ProgressBar current={dayProgress.current} total={dayProgress.total} label={`Overall progress (${dayProgress.date})...`} color="#4e79a7" />}
      {parseProgress && <ProgressBar current={parseProgress.current} total={parseProgress.total} label="Parsing files..." color="#59a14f" />}
      {messages.length > 0 && (
        <div style={{ backgroundColor: '#f5f5f5', padding: '8px 12px', fontSize: '0.8em', maxHeight: '20em', overflow: 'auto', border: '1px solid #ddd', fontFamily: 'monospace' }}>
          {messages.map((m, i) => <div key={i}>{m}</div>)}
        </div>
      )}
    </>
  );
};

/* ---------- Maintenance Tab ---------- */
const MaintenanceTab: React.FC = () => {
  const [stats, setStats] = useState<StatsData | null>(null);
  const [delStart, setDelStart] = useState('');
  const [delEnd, setDelEnd] = useState('');
  const [delCik, setDelCik] = useState('');
  const [msg, setMsg] = useState('');

  const loadStats = () => { institutionApi.stats().then(r => setStats(r.data)).catch(() => {}); };
  useEffect(() => { loadStats(); }, []);

  const handleDeleteRange = async () => {
    if (!delStart || !delEnd || !window.confirm(`Delete all 13F records from ${delStart} to ${delEnd}?`)) return;
    try {
      const r = await institutionApi.deleteByDateRange(delStart, delEnd);
      setMsg(`Deleted ${r.data.deleted} records.`);
      loadStats();
    } catch { setMsg('Delete failed.'); }
  };
  const handleDeleteFiler = async () => {
    if (!delCik.trim() || !window.confirm(`Delete all data for filer CIK ${delCik}?`)) return;
    try {
      const r = await institutionApi.deleteByFiler(delCik.trim());
      setMsg(`Deleted ${r.data.deleted} records.`);
      loadStats();
    } catch { setMsg('Delete failed.'); }
  };
  const handleRefresh = async () => {
    setMsg('Refreshing summary...');
    try {
      const r = await institutionApi.refreshSummary();
      setMsg(`Summary refreshed: ${r.data.rows} rows.`);
      loadStats();
    } catch { setMsg('Refresh failed.'); }
  };
  const handleFixPrn = async () => {
    setMsg('Fixing PRN entries... (this may take a while)');
    try {
      await institutionApi.fixPrn();
      setMsg('PRN fix complete.');
    } catch { setMsg('PRN fix failed.'); }
  };

  return (
    <>
      {stats && (
        <div style={{ backgroundColor: '#f0f7ff', padding: '8px 12px', fontSize: '0.85em', border: '1px solid #cce6ff', marginBottom: '1em' }}>
          <strong>Stats:</strong> {fmt(stats.totalRecords)} total records | {fmt(stats.latestRecords)} latest | {fmt(stats.lastqRecords)} last quarter |
          {fmt(stats.totalFilers)} filers | Date range: {stats.minDate} to {stats.maxDate}
        </div>
      )}
      {msg && <div style={{ padding: '6px 12px', fontSize: '0.85em', backgroundColor: '#ffffdd', border: '1px solid #ccc', marginBottom: '1em' }}>{msg}</div>}

      <div style={{ ...styles.searchBar, marginBottom: '0.5em' }}>
        <strong>Delete by date range: </strong>
        <input type="date" value={delStart} onChange={e => setDelStart(e.target.value)} style={styles.input} />
        <span style={{ margin: '0 0.5em' }}>to</span>
        <input type="date" value={delEnd} onChange={e => setDelEnd(e.target.value)} style={styles.input} />
        <input type="button" value="Delete" onClick={handleDeleteRange} style={{ ...styles.button, marginLeft: '1em', backgroundColor: '#ffcccc' }} />
      </div>
      <div style={{ ...styles.searchBar, marginBottom: '0.5em' }}>
        <strong>Delete by filer CIK: </strong>
        <input value={delCik} onChange={e => setDelCik(e.target.value)} style={{ ...styles.input, width: '12em' }} placeholder="e.g. 0001234567" />
        <input type="button" value="Delete" onClick={handleDeleteFiler} style={{ ...styles.button, marginLeft: '1em', backgroundColor: '#ffcccc' }} />
      </div>
      <div style={{ ...styles.searchBar, marginBottom: '0.5em' }}>
        <strong>Actions: </strong>
        <input type="button" value="Refresh Summary Tables" onClick={handleRefresh} style={{ ...styles.button, marginLeft: '0.5em' }} />
        <input type="button" value="Fix PRN Entries" onClick={handleFixPrn} style={{ ...styles.button, marginLeft: '0.5em' }} />
      </div>
    </>
  );
};

/* ---------- Main Component ---------- */
const InstitutionsPage: React.FC = () => {
  const [tab, setTab] = useState<TabKey>('dashboard');
  const [data, setData] = useState<QuarterlyData | null>(null);
  const [searchData, setSearchData] = useState<SearchData | null>(null);
  const [loading, setLoading] = useState(false);
  const [searchTicker, setSearchTicker] = useState('');

  const loadTab = (t: TabKey) => {
    if (t === 'search' || t === 'dashboard' || t === 'download' || t === 'backfill' || t === 'maintenance') { setLoading(false); return; }
    setLoading(true);
    const fn = t === 'summary' ? institutionApi.quarterlySummary : institutionApi.portfolio;
    fn().then((res) => setData(res.data)).catch(() => {}).finally(() => setLoading(false));
  };

  useEffect(() => { loadTab(tab); }, [tab]);

  const handleSearch = () => {
    if (!searchTicker) return;
    setLoading(true);
    institutionApi.search(searchTicker)
      .then((res) => setSearchData(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  const handleSearchTicker = (ticker: string) => {
    setSearchTicker(ticker);
    setSearchData(null);
    setTab('search');
    setLoading(true);
    institutionApi.search(ticker).then((res) => setSearchData(res.data)).catch(() => {}).finally(() => setLoading(false));
  };

  // Sortable top holders table
  type HolderSortKey = 'filerCIK' | 'filerName' | 'ticker' | 'name' | 'shares' | 'value';
  const [holderSortKey, setHolderSortKey] = useState<HolderSortKey>('shares');
  const [holderSortAsc, setHolderSortAsc] = useState(false);
  const handleHolderSort = (k: HolderSortKey) => { if (holderSortKey === k) setHolderSortAsc(!holderSortAsc); else { setHolderSortKey(k); setHolderSortAsc(k === 'filerCIK' || k === 'filerName' || k === 'ticker' || k === 'name'); } };
  const holderArrow = (k: HolderSortKey) => holderSortKey === k ? (holderSortAsc ? ' \u25B2' : ' \u25BC') : '';
  const sortedHolders = useMemo(() => {
    if (!searchData) return [];
    const a = [...searchData.topHolders]; const d = holderSortAsc ? 1 : -1;
    a.sort((x, y) => { const xv = (x as any)[holderSortKey] ?? ''; const yv = (y as any)[holderSortKey] ?? ''; return xv < yv ? -d : xv > yv ? d : 0; });
    return a;
  }, [searchData, holderSortKey, holderSortAsc]);

  // Sortable logs table
  type LogSortKey = 'quarter' | 'ticker' | 'name' | 'shares';
  const [logSortKey, setLogSortKey] = useState<LogSortKey>('quarter');
  const [logSortAsc, setLogSortAsc] = useState(true);
  const handleLogSort = (k: LogSortKey) => { if (logSortKey === k) setLogSortAsc(!logSortAsc); else { setLogSortKey(k); setLogSortAsc(true); } };
  const logArrow = (k: LogSortKey) => logSortKey === k ? (logSortAsc ? ' \u25B2' : ' \u25BC') : '';
  const sortedLogs = useMemo(() => {
    if (!searchData) return [];
    const a = [...searchData.logs]; const d = logSortAsc ? 1 : -1;
    a.sort((x, y) => { const xv = (x as any)[logSortKey] ?? ''; const yv = (y as any)[logSortKey] ?? ''; return xv < yv ? -d : xv > yv ? d : 0; });
    return a;
  }, [searchData, logSortKey, logSortAsc]);

  const thH = (k: HolderSortKey, label: string) => <th style={{ ...styles.th, cursor: 'pointer', userSelect: 'none' }} onClick={() => handleHolderSort(k)}>{label}{holderArrow(k)}</th>;
  const thL = (k: LogSortKey, label: string) => <th style={{ ...styles.th, cursor: 'pointer', userSelect: 'none' }} onClick={() => handleLogSort(k)}>{label}{logArrow(k)}</th>;

  return (
    <div style={styles.container}>
      <h4 style={styles.heading}>Institutional Ownership (13F)</h4>
      <div style={styles.tabs}>
        {([
          { key: 'dashboard' as TabKey, label: 'Dashboard' },
          { key: 'summary' as TabKey, label: 'Quarterly Summary' },
          { key: 'portfolio' as TabKey, label: 'My Portfolio' },
          { key: 'search' as TabKey, label: 'Ticker Search' },
          { key: 'download' as TabKey, label: 'Download' },
          { key: 'backfill' as TabKey, label: 'Backfill' },
          { key: 'maintenance' as TabKey, label: 'Maintenance' },
        ]).map((t) => (
          <span key={t.key} onClick={() => { setTab(t.key); setData(null); setSearchData(null); }}
            style={{ ...styles.tab, ...(tab === t.key ? styles.activeTab : {}) }}>{t.label}</span>
        ))}
      </div>

      {tab === 'dashboard' && <DashboardTab onSearchTicker={handleSearchTicker} />}

      {tab === 'search' && (
        <div style={styles.searchBar}>
          <label>Ticker: </label>
          <input value={searchTicker} onChange={(e) => setSearchTicker(e.target.value.toUpperCase())}
            onKeyDown={(e) => e.key === 'Enter' && handleSearch()} style={styles.input} />
          <input type="button" value="Search" onClick={handleSearch} style={{ ...styles.button, marginLeft: '1em' }} />
        </div>
      )}

      {loading ? (
        <div style={{ textAlign: 'center', padding: '2em' }}>Loading...</div>
      ) : (tab === 'summary' || tab === 'portfolio') && data ? (
        <>
          <div style={styles.quarterInfo}>Comparing <strong>{data.currentQuarter}</strong> vs <strong>{data.lastQuarter}</strong></div>
          <ChangeTable title="Increased Holdings" items={data.increased} type="increased" tab={tab} onTickerClick={handleSearchTicker} />
          <ChangeTable title="Decreased Holdings" items={data.decreased} type="decreased" tab={tab} onTickerClick={handleSearchTicker} />
          <NewSoldTable title="New Positions" items={data.newPositions} type="new" tab={tab} onTickerClick={handleSearchTicker} />
          <NewSoldTable title="Sold Out" items={data.soldOut} type="sold" tab={tab} onTickerClick={handleSearchTicker} />
          {data.increased.length === 0 && data.decreased.length === 0 && data.newPositions.length === 0 && data.soldOut.length === 0 && (
            <div style={{ textAlign: 'center', padding: '2em', color: '#999' }}>No significant changes found.</div>
          )}
        </>
      ) : tab === 'search' && searchData ? (
        <>
          <div style={styles.quarterInfo}>
            <strong>{searchData.ticker}</strong> &mdash;
            Current holders: <strong>{fmt(searchData.currentHolders)}</strong> |
            Last quarter holders: <strong>{fmt(searchData.lastHolders)}</strong> |
            Current shares: <strong>{fmt(searchData.currentShares)}</strong> |
            Last shares: <strong>{fmt(searchData.lastShares)}</strong>
            {searchData.outstandingShares && <> | Outstanding: <strong>{fmt(searchData.outstandingShares)}</strong></>}
          </div>

          {sortedHolders.length > 0 && (
            <>
              <h4 style={styles.sectionTitle}>Top 20 Holders<PostButton getText={() => tweetTopHolders(searchData.ticker, searchData.topHolders)} /></h4>
              <table style={styles.table}>
                <thead><tr>{thH('filerCIK', 'CIK')}{thH('filerName', 'Filer Name')}{thH('ticker', 'Ticker')}{thH('name', 'Name')}{thH('shares', 'Shares')}{thH('value', 'Value')}</tr></thead>
                <tbody>{sortedHolders.map((h, idx) => (
                  <tr key={idx} style={{ backgroundColor: idx % 2 === 0 ? '#fff' : '#e6f2ff' }}>
                    <td style={styles.td}>{h.filerCIK}</td>
                    <td style={styles.td}>{h.filerName}</td>
                    <td style={styles.td}><a href={yahooUrl(h.ticker)} target="_blank" rel="noreferrer" style={{ color: '#0069cc', textDecoration: 'none', fontWeight: 'bold' }}>{h.ticker}</a></td>
                    <td style={styles.td}>{h.name}</td>
                    <td style={{ ...styles.td, textAlign: 'right' }}>{fmt(h.shares)}</td>
                    <td style={{ ...styles.td, textAlign: 'right' }}>{h.value ? '$' + fmt(h.value) : '-'}</td>
                  </tr>
                ))}</tbody>
              </table>
            </>
          )}

          {sortedLogs.length > 0 && (
            <>
              <h4 style={styles.sectionTitle}>Ownership History</h4>
              <table style={{ ...styles.table, width: '50%' }}>
                <thead><tr>{thL('quarter', 'Quarter')}{thL('ticker', 'Ticker')}{thL('name', 'Name')}{thL('shares', 'Shares')}</tr></thead>
                <tbody>{sortedLogs.map((log, idx) => (
                  <tr key={idx} style={{ backgroundColor: idx % 2 === 0 ? '#fff' : '#e6f2ff' }}>
                    <td style={styles.td}>{log.quarter}</td>
                    <td style={styles.td}><a href={yahooUrl(log.ticker)} target="_blank" rel="noreferrer" style={{ color: '#0069cc', textDecoration: 'none', fontWeight: 'bold' }}>{log.ticker}</a></td>
                    <td style={styles.td}>{log.name}</td>
                    <td style={{ ...styles.td, textAlign: 'right' }}>{fmt(log.shares)}</td>
                  </tr>
                ))}</tbody>
              </table>
            </>
          )}

          <ChangeTable title="Increased Holdings" items={searchData.increased} type="increased" tab={tab} />
          <ChangeTable title="Decreased Holdings" items={searchData.decreased} type="decreased" tab={tab} />
          <NewSoldTable title="New Positions" items={searchData.newPositions} type="new" tab={tab} />
          <NewSoldTable title="Sold Out" items={searchData.soldOut} type="sold" tab={tab} />
        </>
      ) : null}

      {tab === 'download' && <DownloadTab />}
      {tab === 'backfill' && <BackfillTab />}
      {tab === 'maintenance' && <MaintenanceTab />}
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  container: { padding: '1em 0.5em', backgroundColor: '#fff', minHeight: 'calc(100vh - 6em)' },
  heading: { color: '#0069cc', marginTop: '1em', textAlign: 'center' },
  tabs: { display: 'flex', gap: '0', marginBottom: '1em', borderBottom: '2px solid #0d58e6' },
  tab: { padding: '6px 16px', cursor: 'pointer', fontSize: '0.85em', fontWeight: 'bold', backgroundColor: '#e6f2ff', border: '1px solid #0d58e6', borderBottom: 'none', marginRight: '2px' },
  activeTab: { backgroundColor: '#cce6ff', color: '#000' },
  searchBar: { backgroundColor: '#ececec', padding: '8px 12px', fontSize: '0.85em', marginBottom: '1em' },
  input: { padding: '2px 4px', fontFamily: 'Arial, sans-serif', fontSize: '1em', width: '10em' },
  button: { padding: '4px 16px', backgroundColor: '#ccdcfc', color: '#000', border: '1px solid #808080', cursor: 'pointer', fontFamily: 'Arial, sans-serif', fontSize: '1em' },
  quarterInfo: { backgroundColor: '#f0f7ff', padding: '8px 12px', fontSize: '0.85em', marginBottom: '1em', border: '1px solid #cce6ff' },
  sectionTitle: { fontSize: '0.9em', margin: '1em 0 0.3em', color: '#333' },
  table: { width: '100%', marginTop: '0.3em', marginBottom: '1em', border: '2px solid #0d58e6', borderCollapse: 'collapse' as const },
  th: { border: '1px solid #0d58e6', padding: '4px 6px', backgroundColor: '#cce6ff', fontWeight: 'bold', fontSize: '0.8em', textAlign: 'center' as const },
  td: { border: '1px solid #0d58e6', padding: '3px 6px', fontSize: '0.8em' },
  actionLink: { color: '#0069cc', textDecoration: 'none', cursor: 'pointer', fontSize: '0.9em' },
};

export default InstitutionsPage;
